import React, { createRef, useEffect } from 'react';
import styled from 'styled-components/macro';
import {
  TabWrapper,
  ModalHeader,
  HeaderSide,
  LinkWrapper,
  LinkBtn,
  HeaderCenter,
  ModalBody,
  ShareByEmbedding,
  getTabs,
} from '../';
import { useAuth } from 'lib/context';
import { MdClose } from 'react-icons/md';
import { ButtonPillSwitch } from 'lib/components';
import { useGetQuickshareDetails } from 'lib/api/quickshare/useGetQuickshareDetails';
import { VideoPlayer } from 'app/pages/video/videoPlayer';
import { useSendAndShare } from 'lib/context/send-and-share/provider';
import { useGTMTrackPageView } from 'lib/hooks/GTM/useGTMTrackPageView';
import { useVideoMessageInteractionCalculation } from 'lib/hooks/GTM/actions/video/useVideoMessageInteractionCalculation';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import { GTMVideoMessageInteractionAction } from 'lib/hooks/GTM/actions/video';
import { TRACKED_PAGES } from 'lib/hooks/GTM/utils';
import { useTranslation } from 'react-i18next';
import { CovideoGTMVehicleItem } from 'lib/hooks/GTM/types';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  box-sizing: border-box;
  width: 592px;
  max-width: 100%;
  margin: 0 auto;
  padding-bottom: 50px;
`;

const Card = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.secondary.non_alpha};
  border: 1px solid ${({ theme }) => theme.colors.neutral[20]};
  border-radius: 7px;
`;

const VideoWrapper = styled.div`
  .fullscreen {
    height: auto;
  }
`;

type Props = {
  setActiveTab: (tab: string) => void;
  handleModalClose: () => void;
};

export const EmbedTab = ({ setActiveTab, handleModalClose }: Props) => {
  const { video } = useSendAndShare();
  const { t } = useTranslation();
  const { data: quickshareDetails, isFetching: videoShareLinkLoading } =
    useGetQuickshareDetails({ videoId: video.id });
  const { trackVideoSpecificPagesManually } = useGTMTrackPageView();
  const { GTMVideoAttributes, GTMVideoMessageProperties } =
    useVideoMessageInteractionCalculation({});
  const { trackGTMVideoMessageInteractionEvent } = useGTMAnalytics();
  const { userData } = useAuth();
  const tabs = getTabs(userData, t);

  const videoShareLink = quickshareDetails?.data || { html: '', url: '' };

  const videoRef = createRef<HTMLVideoElement>();

  useEffect(() => {
    trackVideoSpecificPagesManually?.({
      ...TRACKED_PAGES.EMBED_SEND_AND_SHARE,
      videoAttributes: GTMVideoAttributes,
      vehicle: video.videoRequest?.inventoryItem as CovideoGTMVehicleItem,
    });
  }, [video.id]);

  return (
    <TabWrapper display='block'>
      <ModalHeader>
        <HeaderSide>
          <LinkWrapper>
            <LinkBtn onClick={handleModalClose}>
              <MdClose size='18' />
              <span>{t('Close')}</span>
            </LinkBtn>
          </LinkWrapper>
        </HeaderSide>
        <HeaderCenter>
          <ButtonPillSwitch
            defaultValue={tabs.embed.value}
            values={tabs}
            onChange={newTab => setActiveTab(newTab)}
          />
        </HeaderCenter>
        <HeaderSide></HeaderSide>
      </ModalHeader>
      <ModalBody>
        <ContentWrapper>
          <Card>
            <ShareByEmbedding
              videoShareLink={videoShareLink}
              disableShare={videoShareLinkLoading}
              gtmVideoMessageInteractionEvent={() =>
                trackGTMVideoMessageInteractionEvent?.({
                  action: GTMVideoMessageInteractionAction.EMBED,
                  video: GTMVideoAttributes,
                  videoMessageProperties: GTMVideoMessageProperties,
                  vehicle: video.videoRequest
                    ?.inventoryItem as CovideoGTMVehicleItem,
                })
              }
            />
          </Card>
          <VideoWrapper>
            <VideoPlayer
              videoId={video.id}
              videoRef={videoRef}
              autoplay={false}
              videoSource={video.videoSource}
            />
          </VideoWrapper>
        </ContentWrapper>
      </ModalBody>
    </TabWrapper>
  );
};
